import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { CapacityDefaults, CpuDefaults, Relay } from "../api/types";
import { CopyBlock } from "../components/ui/CopyBlock";

/**
 * What a guide sentence may quote about this deployment, all optional.
 *
 * Every figure here is the server's (`GET /api/v1/capacity`), never typed into
 * this file: a number written here would go stale the day the deployment
 * changes and the guide would be the one screen that lies. When a figure is
 * missing the sentence degrades to words — the guide never invents one, and it
 * renders in full either way, because the moment the cluster cannot be reached
 * is exactly the moment somebody opens the manual.
 */
export interface GuideNumbers {
  /** `capacity.defaults`, or `null` while the console has not answered. */
  readonly gpu: CapacityDefaults | null;
  /** `capacity.cpu.defaults`, or `null` without a CPU fleet or an answer. */
  readonly cpu: CpuDefaults | null;
  /** `capacity.node_start_eta_minutes` — "usually about" wording only. */
  readonly bootEtaMinutes: number | null;
  /**
   * `GET /api/v1/relay`, or `null` while it has not answered.
   *
   * The MLflow tutorial quotes its published lines — the tunnel command, the
   * export, the plain ssh line — exactly as the server built them, for the
   * same reason the Training elsewhere screen does: a guide that assembled
   * its own would be inventing an address the day the configuration moves.
   */
  readonly relay: Relay | null;
  /**
   * `auth config`'s `access_contact` — who to ask when stuck, published by
   * the server so the guide never hard-codes a person.
   */
  readonly contact: string | null;
  /**
   * `GET /api/v1/me`'s `slack_notifications` — whether this console has a
   * Slack app to send warnings through.
   *
   * The one sentence in this manual that must never be wrong is where a
   * warning arrives, so it is the server's answer and not this file's guess.
   * False keeps the sentence the console shipped with — the page is where a
   * warning appears — which is the safe half of the pair: somebody who
   * watches the page loses nothing, somebody promised a message that never
   * comes loses a workspace.
   */
  readonly slackNotifications: boolean;
}

/** The guide's chapters, in reading order. Rendered as the nav's groups. */
export const GUIDE_GROUPS = [
  "First day",
  "Everyday",
  "The CPU fleet",
  "Reference",
] as const;

export type GuideGroup = (typeof GUIDE_GROUPS)[number];

/**
 * One section of the guide — one dedicated page under `/docs/<id>`.
 *
 * `id` is the route segment and the nav's key; `group` places it in the nav
 * and on the index. `body` receives the live numbers and returns the whole
 * tutorial.
 */
export interface GuideSection {
  readonly id: string;
  readonly title: string;
  /** One plain sentence under the heading — what this section teaches. */
  readonly lead: string;
  readonly group: GuideGroup;
  readonly body: (numbers: GuideNumbers) => ReactNode;
}

/** `[1, 2, 4, 8]` → "1, 2, 4 or 8", so a list reads as a sentence. */
function spelled(counts: readonly number[]): string {
  if (counts.length === 0) {
    return "";
  }
  if (counts.length === 1) {
    return String(counts[0]);
  }
  return `${counts.slice(0, -1).join(", ")} or ${counts[counts.length - 1]}`;
}

/** A command or path in running text. */
function Mono({ children }: { children: ReactNode }): JSX.Element {
  return <span className="mono">{children}</span>;
}

/**
 * What a command answers, shown as illustration — never copyable, because
 * nobody should paste an output.
 */
function Output({ children }: { children: string }): JSX.Element {
  return <pre className="docs-output">{children}</pre>;
}

/**
 * The manual, section by section — each one a page of its own.
 *
 * Sources: `docs/ONBOARDING.md`, `docs/PRODUCT.md`, `cli/README.md` and the
 * workspace image (`backend/workspace-image/`) — rewritten as tutorials, not
 * copied. The rules every string here follows: plain sentences a research
 * scientist reads without translating, no cluster vocabulary, no invented
 * number (see `GuideNumbers`), and nothing another screen publishes better —
 * connection lines belong to the workspace screen, tunnel lines to
 * <em>Training elsewhere</em>, the environment list to its own page.
 *
 * This module holds content only — no hooks, no fetching — so any screen can
 * render a section of it without carrying the docs pages' dependencies.
 */
export const GUIDE: readonly GuideSection[] = [
  // ------------------------------------------------------------- first day
  {
    id: "getting-started",
    title: "Getting started",
    lead: "From zero to a shell on a GPU machine — about ten minutes, once.",
    group: "First day",
    body: ({ gpu, bootEtaMinutes }) => (
      <>
        <p>
          The console is one place to take machines from and give them back.
          You ask for GPUs, work, and stop; your files survive everything.
          Here is the whole first day, step by step.
        </p>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Sign in</strong> with your Neuralk Google account.
              Nothing to install, no new password. The first time, your
              account waits for approval — the screen names the person to
              ping, and it takes them a minute.
            </p>
          </li>
          <li>
            <p>
              <strong>Make an SSH key</strong>, if you do not already have
              one. In a terminal on your laptop, run this and press Enter
              three times:
            </p>
            <CopyBlock
              value="ssh-keygen -t ed25519"
              label="Copy the ssh-keygen command"
            />
            <p>
              This creates two files. The one ending in <Mono>.pub</Mono> is
              public and gets pasted in the next step; the other one never
              leaves your laptop.
            </p>
          </li>
          <li>
            <p>
              <strong>Register the key</strong> on the{" "}
              <Link to="/account">Account page</Link>. Print it, copy the
              whole line, paste it there:
            </p>
            <CopyBlock
              value="cat ~/.ssh/id_ed25519.pub"
              label="Copy the command that prints your public key"
            />
            <p>
              A key carries a scope: <em>workspaces</em> opens the machines
              this console starts; <em>training elsewhere</em> also installs
              it on the machine the{" "}
              <Link to="/relay">Training elsewhere</Link> page describes. One
              key per machine you connect from, named for that machine, lets
              you revoke one laptop without touching the others.
            </p>
          </li>
          <li>
            <p>
              <strong>Ask for GPUs.</strong> Big button, top of the{" "}
              <Link to="/">home screen</Link>. Three choices and nothing
              else: how many GPUs
              {gpu ? <> ({spelled(gpu.allowed_gpu_counts)})</> : null}, for
              how long, and which environment. If GPUs are free you have
              them in seconds; if nothing is free, a machine is started{" "}
              <em>for you</em>
              {bootEtaMinutes === null ? (
                <> — live progress on screen, nothing to babysit.</>
              ) : (
                <>
                  {" "}
                  — usually about {bootEtaMinutes} minutes, live progress on
                  screen, nothing to babysit.
                </>
              )}
            </p>
          </li>
          <li>
            <p>
              <strong>Connect.</strong> The workspace screen shows the exact{" "}
              <Mono>ssh</Mono> line to paste — copy it, run it, you are in:
              on your GPUs, as yourself, in your own <Mono>/home</Mono>. If
              the very first attempt says{" "}
              <Mono>Connection refused</Mono>, wait a minute and try again —
              the door opens up to a minute after a brand-new workspace, and
              only then.
            </p>
          </li>
          <li>
            <p>
              <strong>Check the GPUs are yours:</strong>
            </p>
            <CopyBlock value="nvidia-smi" label="Copy nvidia-smi" />
            <p>
              It lists exactly the GPUs you asked for. <Mono>sudo</Mono>{" "}
              works whenever you need root — logging in <em>as</em> root is
              switched off; <Mono>sudo</Mono> is how you install system
              packages.
            </p>
          </li>
          <li>
            <p>
              <strong>Stop when you are done, every time.</strong> The
              machine winds down and stops costing anything; your files do
              not move, and your next workspace — ten minutes or ten days
              later — opens on them exactly as you left them. Getting back
              in is one click and a short wait: that is the whole reason
              this beats keeping a VM alive.
            </p>
          </li>
        </ol>
        <p>
          Next: set up{" "}
          <Link to="/docs/python-envs">your Python environment</Link> once,
          so it follows you to every machine.
        </p>
      </>
    ),
  },
  // -------------------------------------------------------------- everyday
  {
    id: "python-envs",
    title: "Python, with uv",
    lead: "Set your environment up once, in /home — it follows you to every machine.",
    group: "Everyday",
    body: () => (
      <>
        <p>
          There are two Pythons on a workspace, and knowing which is which
          saves an afternoon. The <em>machine's own</em> Python belongs to
          its operating system: <Mono>pip install</Mono> into it answers{" "}
          <Mono>externally-managed-environment</Mono> and refuses, which is
          fine — that was never the Python to work in. <em>Yours</em> lives
          in <Mono>/home</Mono>, built with <Mono>uv</Mono>, and it
          survives every workspace because <Mono>/home</Mono> does.{" "}
          <Mono>uv</Mono> is preinstalled; <Mono>uv --version</Mono> says
          which one this environment carries — if it is older than your
          laptop's and settings seem ignored, say so and the image is
          rebuilt.
        </p>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Install an interpreter of your own</strong> — on the
              bare CUDA environment there is none at all, and doing it
              everywhere is a good habit anyway:
            </p>
            <CopyBlock
              value="uv python install 3.12"
              label="Copy the interpreter install command"
            />
            <p>
              It lands under <Mono>/home</Mono>, so it survives a change
              of machine <em>and</em> a change of environment image.
            </p>
          </li>
          <li>
            <p>
              <strong>Create the environment, inside your project:</strong>
            </p>
            <CopyBlock
              value={"mkdir -p ~/my-project && cd ~/my-project\nuv venv --python 3.12"}
              label="Copy the environment creation commands"
              multiline
            />
            <p>
              It lands in <Mono>~/my-project/.venv</Mono> — on{" "}
              <Mono>/home</Mono>, so it is still there tomorrow, on
              whatever machine your next workspace opens on.
            </p>
          </li>
          <li>
            <p>
              <strong>Activate it and install:</strong>
            </p>
            <CopyBlock
              value={"source .venv/bin/activate\nuv pip install torch transformers"}
              label="Copy the activate and install commands"
              multiline
            />
            <p>
              <Mono>uv pip install</Mono> takes everything{" "}
              <Mono>pip install</Mono> does — a{" "}
              <Mono>requirements.txt</Mono>, a local{" "}
              <Mono>pyproject.toml</Mono>, an editable <Mono>-e .</Mono>
            </p>
          </li>
          <li>
            <p>
              <strong>Command-line tools</strong> get their own homes with:
            </p>
            <CopyBlock
              value="uv tool install ruff"
              label="Copy the tool install command"
            />
            <p>
              They land in <Mono>~/.local/bin</Mono>, which is first on
              your PATH — so a tool you install always wins over the
              image's copy.
            </p>
          </li>
        </ol>
        <p>
          Conda works the same way if you prefer it — put the environments
          under <Mono>/home</Mono> and they follow you. What does{" "}
          <em>not</em> follow you is <Mono>sudo apt install</Mono>: that
          writes into the machine's own system, which is rebuilt from the
          environment image at every start. Fine for trying something out;
          anything you want to keep belongs in <Mono>/home</Mono> — or in{" "}
          <Link to="/docs/environments">an image of your own</Link>.
        </p>
        <p>
          One shell habit worth knowing: if you install a newer version of
          a tool and the old one keeps answering, run <Mono>rehash</Mono>{" "}
          (<Mono>hash -r</Mono> in bash) or open a new terminal — details
          in <Link to="/docs/troubleshooting">Troubleshooting</Link>.
        </p>
      </>
    ),
  },
  {
    id: "gpu-workspaces",
    title: "GPU workspaces",
    lead: "Sizes, comfortable connections, changing size, and the two clocks.",
    group: "Everyday",
    body: ({ gpu, slackNotifications }) => (
      <>
        <h2>Choosing a size</h2>
        <p>
          {gpu ? (
            <>You can ask for {spelled(gpu.allowed_gpu_counts)} GPUs — the </>
          ) : (
            <>The </>
          )}
          biggest choice is a whole machine, the most one workspace can
          physically use. The console starts the smallest machine that
          covers your ask, so a small request does not light up a big
          machine for nothing. The form says which sizes start instantly and
          which need a machine to boot first.
        </p>
        <p>
          When the fleet holds more than one <em>kind</em> of GPU, the form
          first asks which — each kind with its own sizes and its own
          machines, and the console never serves your ask with the other
          one. The choice is fixed for the workspace's life: wanting the
          other GPU means stopping this workspace and opening the one you
          mean. With one kind, the question is not asked at all.
        </p>
        <h2>Connecting comfortably</h2>
        <p>
          The workspace screen gives you three ways in: the plain{" "}
          <Mono>ssh</Mono> line, an <em>Open in VS Code</em> button that
          connects the editor straight to the workspace, and the{" "}
          <Mono>~/.ssh/config</Mono> block if you prefer setting the alias
          up by hand. For Jupyter or anything listening on a port, append a
          forward to your ssh line — <Mono>-L 8888:localhost:8888</Mono> —
          and open <Mono>localhost:8888</Mono> in your browser.
        </p>
        <h2>Changing size mid-work</h2>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Checkpoint into <Mono>/home</Mono> first — not{" "}
              <Mono>/scratch</Mono>.</strong> Changing size restarts the
              environment: anything running stops, and{" "}
              <Mono>/scratch</Mono> is wiped with the machine.
            </p>
          </li>
          <li>
            <p>
              <strong>Click Change GPUs</strong> on the workspace screen and
              pick the new size. Giving back is always quick — you hold
              those GPUs. Taking <em>more</em> can mean waiting for a
              machine, and honesty requires the whole sentence: the
              workspace gives its GPUs up first, and if no machine turns up
              within the waiting limit the request is dropped and the
              workspace ends — your <Mono>/home</Mono> is intact, but you
              reopen rather than resume. When the fleet is busy, finishing
              at the size you have — or stopping and opening a new
              workspace at the size you need — is often the better trade.
            </p>
          </li>
          <li>
            <p>
              <strong>Reconnect with the same line.</strong> Your files are
              in <Mono>/home</Mono> either way; nothing to copy.
            </p>
          </li>
        </ol>
        <h2>The two clocks</h2>
        <p>
          A workspace has a <strong>deadline</strong>
          {gpu ? <> — {gpu.workspace_hours} hours unless you chose otherwise</> : null}
          , shown on its screen, and warns you there as the end approaches.{" "}
          <em>Keep it longer</em> adds hours in one click, as many times as
          the work needs — there is no maximum for a GPU workspace,
          deliberately. Finished early? <em>Give time back</em> moves the
          deadline in while everything keeps running.
        </p>
        <p>
          <strong>GPUs measured doing nothing are taken back.</strong>{" "}
          {gpu ? (
            <>
              No GPU activity for {gpu.idle_threshold_minutes} minutes
              brings a warning and a {gpu.idle_grace_minutes}-minute
              countdown, then the workspace stops
            </>
          ) : (
            <>
              After a stretch of no GPU activity comes a warning, a short
              countdown, then the workspace stops
            </>
          )}{" "}
          — your <Mono>/home</Mono> survives, as always. Any GPU activity,
          or <em>Keep it longer</em>, cancels the countdown.
        </p>
        <p className="docs-callout">
          {slackNotifications ? (
            <>
              <strong>
                Both warnings appear on the console page and arrive as a
                Slack message.
              </strong>{" "}
              The message comes from <em>GPU Console</em>, to the address you
              sign in with, and carries a link to the workspace page, where{" "}
              <em>Keep it longer</em> is one click. If Slack refuses it — no
              account on that address, Slack down — the workspace's{" "}
              <strong>What has happened</strong> trace says so: a warning is
              never silently lost. Nothing is printed into your SSH session or
              VS Code either way.
            </>
          ) : (
            <>
              <strong>
                Both warnings appear on the console page, and nowhere else.
              </strong>{" "}
              Nothing is printed into your SSH session or VS Code. Keep the
              workspace page open in a tab.
            </>
          )}{" "}
          Click <em>Keep it longer</em> before a long CPU-only stretch —
          downloads and tokenizing are idle GPUs by the console's measure,
          even though you are working.
        </p>
        <p>
          Your <Link to="/account#account-usage">Account page</Link> tells
          you what you held over the last week, month or quarter, how much of
          it was computing, and what it cost. It is information, nothing more
          — the idle share is the number worth lowering, and the console never
          refuses you GPUs because of it.
        </p>
      </>
    ),
  },
  {
    id: "memory",
    title: "Memory: what comes with a GPU",
    lead: "What your workspace holds, what happens when a program goes past it, and why free lies to you.",
    group: "Everyday",
    body: ({ gpu }) => {
      // The figures are the server's or they are not said at all. `gpu` being
      // there proves nothing about `per_gpu`: this bundle can be newer than
      // the console it is talking to, and every sentence below has a
      // word-only form for that case. An "undefined GiB" never reaches a page.
      const per = gpu?.per_gpu ?? null;
      return (
        <>
          <p>
            Every GPU you ask for comes with a fixed share of the machine
            {per ? (
              <>
                {" "}
                — {per.cores} cores and {per.memory_gb} GiB of memory. Two GPUs
                is twice both.
              </>
            ) : (
              <>
                : a number of cores and an amount of memory, both set by this
                deployment and shown on your workspace page. Two GPUs is twice
                both.
              </>
            )}{" "}
            The two are not the same kind of promise. The cores are a{" "}
            <strong>floor</strong>: they are yours, nobody can take them, and
            your work may use more than that while the machine is quiet — which
            is why a data loader is never throttled. The memory is a floor{" "}
            <strong>and a ceiling</strong>: you always have it, and you cannot
            go past it.
          </p>
          <h2>When a program goes past it</h2>
          <p>
            Everything running in the workspace stops at the same moment — your
            script and the shell you were typing in. From your side it looks
            like the connection dropped for no reason. That is the machine
            protecting your neighbour: one workspace taking all the memory would
            take their work down with it.
          </p>
          <p>
            <strong>The workspace itself survives.</strong> It starts again on
            the same machine, with the same GPUs and the same connection line —
            reconnect the same way you did before, by SSH or from VS Code, and
            carry on. <Mono>/home</Mono> and <Mono>/scratch</Mono> are exactly
            as you left them; only what was in memory is gone, so a notebook
            kernel or a <Mono>tmux</Mono> session has to be started again. The{" "}
            <strong>What has happened</strong> trace on the workspace page says
            that it happened, and when.
          </p>
          <p>
            <strong>The log starts again with it.</strong> The <em>Log</em>{" "}
            panel on the workspace page shows what the workspace has printed{" "}
            <em>since</em> it restarted, so the traceback that was on screen
            when everything stopped is not in it. If you want to read it
            afterwards, keep your own copy while it runs —{" "}
            <Mono>{"python train.py 2>&1 | tee /home/$USER/train.log"}</Mono>{" "}
            — and it is waiting in <Mono>/home</Mono> whatever happens.
          </p>
          <h2>Two different ways to run out</h2>
          <p>
            <Mono>torch.cuda.OutOfMemoryError</Mono> is the <em>GPU&apos;s</em>{" "}
            memory: an ordinary Python error, your script stops, your shell
            stays. Running out of the <em>machine&apos;s</em> memory — a dataset
            read into RAM, too many loader workers — is the one above, and it
            takes the shell with it. If you have seen both and thought they were
            the same thing, that is why one of them only printed an error.
          </p>
          <h2>free shows the whole machine, not your share</h2>
          <p>
            <Mono>free -g</Mono> and <Mono>htop</Mono> report the machine&apos;s
            memory — hundreds of GiB, most of it somebody else&apos;s — exactly
            as <Mono>df</Mono> does for <Mono>/scratch</Mono>. Your share is the
            figure on your workspace page. Watch your own processes instead:
          </p>
          <CopyBlock
            value={"ps -o rss=,comm= -u $(whoami) --sort=-rss | head"}
            label="Copy the command that lists what your processes hold"
          />
          <p>The first column is in kilobytes.</p>
          <h2>If you need more</h2>
          <p>
            Ask for more GPUs — memory scales with the count, and{" "}
            <em>Change GPUs</em> does it in one click
            {per ? (
              <>
                , so two GPUs come with {2 * per.memory_gb} GiB
              </>
            ) : null}
            . Or read the data in chunks, or memory-map it, and never hold the
            whole set at once.
          </p>
        </>
      );
    },
  },
  {
    id: "files",
    title: "Where your files live",
    lead: "One folder is yours forever, one is the team's; the rest belongs to the machine.",
    group: "Everyday",
    body: ({ gpu }) => (
      <>
        <ul>
          <li>
            <Mono>/home/you</Mono> — <strong>yours, forever.</strong> Code,
            environments, notebooks, shell history, dotfiles. Survives
            stopping, expiry, and the machine being replaced.
          </li>
          <li>
            <Mono>/datasets</Mono> — <strong>shared, read-only.</strong> The
            team&apos;s datasets, the same on every machine, already there when
            your workspace opens. You cannot write to it — results go to{" "}
            <Mono>/home/you</Mono> or <Mono>/scratch</Mono>. To add or refresh
            a dataset, ask — it lands once, for everyone.
          </li>
          <li>
            <Mono>/scratch</Mono> — <strong>fast local disk, temporary.</strong>{" "}
            Checkpoints and intermediates during a run. Wiped when the
            workspace stops — and when you change its size. It has a
            ceiling that scales with the GPUs or cores you hold
            {gpu?.per_gpu ? <> — {gpu.per_gpu.scratch_gb} GiB per GPU</> : null}
            , and crossing it stops <em>your</em> workspace: the disk is
            shared with the neighbouring one. <Mono>df</Mono> will mislead
            you — it reports the machine's whole disk, not your share, the
            same way <Mono>free</Mono> does for memory (
            <Link to="/docs/memory">Memory</Link>); watch{" "}
            <Mono>du -sh /scratch</Mono> and rotate old checkpoints out.
          </li>
          <li>
            <Mono>/dev/shm</Mono> — <strong>shared memory, as large as the
            memory you hold.</strong> PyTorch data loaders with{" "}
            <Mono>num_workers &gt; 0</Mono> pass batches through it. Most
            containers get 64 MB there and workers die with{" "}
            <Mono>Bus error</Mono>; yours gets your whole memory limit, so
            that error here means the batch really did not fit.
          </li>
          <li>
            <Mono>/work</Mono> — belongs to{" "}
            <Link to="/docs/runs">runs of more than one task</Link> only:
            one shared folder across a run's machines, deleted when the run
            ends. It never appears in a workspace.
          </li>
        </ul>
        <p>
          Rule of thumb: <strong>if losing it would annoy you, it belongs
          in <Mono>/home/you</Mono>.</strong>
        </p>
        <p>
          The other way round too: a copy of a shared dataset you have been
          keeping in <Mono>/home</Mono> can go — <Mono>/datasets</Mono> has it,
          on every machine, and it is not taking up your space.
        </p>
        <p>
          What survives between workspaces is exactly what sits in{" "}
          <Mono>/home</Mono>: your uv and conda environments, tools
          installed with <Mono>uv tool install</Mono> or{" "}
          <Mono>npm install -g</Mono> (both land under{" "}
          <Mono>~/.local</Mono>), your <Mono>~/.zshrc</Mono>. What does
          not: <Mono>sudo apt install</Mono> and anything else written
          outside your home — the machine is rebuilt from the environment
          image at every start.
        </p>
        <p>
          Two things nobody expects: coming back to a stopped workspace
          never greets you with a scary{" "}
          <Mono>REMOTE HOST IDENTIFICATION HAS CHANGED</Mono> — the
          machine's identity travels with your home — and your workspace is{" "}
          <strong>not private</strong> from teammates on the same machine,
          so do not put anything there you would not show a colleague.
        </p>
      </>
    ),
  },
  {
    id: "jobs",
    title: "Batch jobs",
    lead: "A workspace without the shell: it runs your command to the end, you read the log.",
    group: "Everyday",
    body: () => (
      <>
        <p>
          A <Link to="/jobs">job</Link> takes the same GPUs, the same{" "}
          <Mono>/home/you</Mono> and the same environment as a workspace —
          but opens no shell and needs nobody connected. The right shape
          for long unattended runs.
        </p>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Prepare everything from a workspace first.</strong>{" "}
              The job's command runs in <Mono>/home/you</Mono>, on exactly
              the files your workspace sees — code, data and your Python
              environment must already be there. The first time, clone your
              repositories from inside a workspace connecting with{" "}
              <Mono>ssh -A</Mono> (add <Mono>-A</Mono> to the line the
              workspace screen gives you): your GitHub key stays on your
              laptop and the clone uses it through the agent, so private
              repositories just work. Load the key into your agent first —{" "}
              <Mono>ssh-add ~/.ssh/id_ed25519</Mono>, then{" "}
              <Mono>ssh-add -l</Mono> to check — and use <Mono>-A</Mono>{" "}
              for the clone only, connecting without it afterwards: a
              forwarded agent can be used by anyone with root on that
              machine, and your teammates have <Mono>sudo</Mono> there too.
            </p>
          </li>
          <li>
            <p>
              <strong>Submit it</strong> on the <Link to="/jobs">Jobs</Link>{" "}
              page: the command, how many GPUs, for how long. Point the
              command at your own environment explicitly — the job does not
              read your shell configuration:
            </p>
            <CopyBlock
              value="cd ~/my-project && .venv/bin/python train.py --epochs 50"
              label="Copy an example job command"
            />
          </li>
          <li>
            <p>
              <strong>Read the log on the job's page</strong> — live while
              it runs, kept after it ends. Close your laptop; nothing
              depends on you staying connected.
            </p>
          </li>
          <li>
            <p>
              <strong>It ends by itself</strong> when the command exits, or
              at its deadline — extend from the job's page if the work
              needs longer.
            </p>
          </li>
        </ol>
      </>
    ),
  },
  {
    id: "environments",
    title: "Environments",
    lead: "Most people never need one — and adding your own is one paste.",
    group: "Everyday",
    body: () => (
      <>
        <p>
          An environment is the machine's starting image — what is
          installed <em>outside</em> your home. Most people never need a
          custom one: your packages live in{" "}
          <Link to="/docs/python-envs">your own /home environment</Link>{" "}
          and survive every workspace, whatever image it starts from. The{" "}
          <Link to="/environments">Environments page</Link> lists what is
          on offer, including what teammates have shared.
        </p>
        <p>
          Reach for an image of your own when the <em>system</em> layer is
          what you need: a different CUDA, a system library, a tool that
          must exist before you connect.
        </p>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Build it on your own machine</strong> — you cannot
              build an image from inside a workspace. Start from the
              workspace image recipe (
              <Mono>backend/workspace-image/README.md</Mono> in the
              gpu-console repository): that recipe is what gives a
              workspace its SSH door, and an image built without it opens
              no shell.
            </p>
          </li>
          <li>
            <p>
              <strong>Push it</strong> to the team registry, or anywhere
              public. The machines must be able to download it; only a
              short list of registries is accepted, and the error names
              them if yours is not on it.
            </p>
          </li>
          <li>
            <p>
              <strong>Paste the reference</strong> (
              <Mono>registry/repository:tag</Mono>) on the{" "}
              <Link to="/environments">Environments page</Link> and name
              it. Tick <em>Share with the team</em> and everybody can pick
              it — it stays yours to change or delete.
            </p>
          </li>
          <li>
            <p>
              <strong>If a workspace using it dies at start</strong> with
              "the environment's image could not be downloaded" in its
              What happened trace: the reference is wrong, the tag was
              never pushed, or the machines may not pull from that
              registry. That one is yours to fix — check the reference
              first.
            </p>
          </li>
        </ol>
        <p>
          One nuance: a <em>job</em> can run any image that can be
          downloaded; a <em>workspace</em> image must carry the SSH door
          from the recipe, or there is nothing to connect to.
        </p>
      </>
    ),
  },
  // --------------------------------------------------------- the CPU fleet
  {
    id: "cpu-workspaces",
    title: "CPU workspaces",
    lead: "The same workspace, sized in cores — with one rule of its own.",
    group: "The CPU fleet",
    body: ({ gpu, cpu }) => (
      <>
        <p>
          The <Link to="/cpu">CPU page</Link> opens workspaces on the CPU
          fleet
          {cpu && cpu.allowed_workspace_cores.length > 0 ? (
            <> — {spelled(cpu.allowed_workspace_cores)} cores — </>
          ) : (
            <> </>
          )}
          with the same SSH line, the same <Mono>/home</Mono> and the same
          environments as a GPU one. The cores you ask for are yours alone.
        </p>
        <p className="docs-callout">
          <strong>A CPU workspace is never stopped for being idle.</strong>{" "}
          Nothing can tell a busy core from a stuck one, so it runs — and
          costs — until its deadline or until you stop it. Set the deadline
          you mean, and stop it when you are done: those are the only two
          controls, and they are both yours. Because nothing else bounds
          it, its deadline is also capped <em>in total</em>
          {gpu ? <> — {gpu.max_workspace_hours} hours</> : null} — unlike a
          GPU workspace, where extending has no end because idle GPUs go
          back by themselves. And there is no changing size here: to get
          more cores, stop it and open a bigger one — your{" "}
          <Mono>/home</Mono> is untouched either way.
        </p>
        <p>
          The CPU page also offers a <em>terminal to drive runs from</em> —
          the smallest workspace there is
          {cpu && cpu.allowed_workspace_cores.length > 0 ? (
            <>
              {" "}
              ({Math.min(...cpu.allowed_workspace_cores)}{" "}
              {Math.min(...cpu.allowed_workspace_cores) === 1
                ? "core"
                : "cores"}
              )
            </>
          ) : null}
          , whose whole job is submitting and watching{" "}
          <Link to="/docs/runs">runs</Link> with <Mono>nk</Mono>, already
          installed and already pointed at this console.
        </p>
        <p>
          One consequence of your files being a single disk on one machine
          at a time: a GPU workspace and a CPU workspace cannot be open at
          once — the second ask is refused immediately, naming what holds
          your files. Runs of more than one task are the exception: they
          never mount your home, so you may fan out while sitting in a
          workspace.
        </p>
      </>
    ),
  },
  {
    id: "runs",
    title: "Runs: one command, many times",
    lead: "Fan a command out across machines, watch it move, get the results back.",
    group: "The CPU fleet",
    body: ({ cpu }) => (
      <>
        <p>
          A <strong>run</strong> is one command, a size in cores, and a
          number of copies: <em>run this, on 4 cores, 250 times</em>. The
          console starts the machines, spreads the copies across them, and
          gives the machines back. Each copy is a <strong>task</strong> and
          knows which one it is from <Mono>$TASK_ID</Mono>.
        </p>
        <p>
          Two roads in. The form on the <Link to="/cpu">CPU page</Link> —
          command, cores, copies, hours — needs nothing installed and is
          the right one for a first run. <Mono>nk</Mono> is the same run
          from a terminal, the road you want once the run is a line in a
          script; it is what the rest of this section uses.
        </p>

        <h2>Your first run, in five steps</h2>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Create a console key</strong>, once, on the{" "}
              <Link to="/account">Account page</Link> — beside your SSH
              key, but a different thing: it names your account when a
              terminal talks to the console. It is shown once, so copy it
              then; revoke it from the same page the day that laptop goes
              away.
            </p>
          </li>
          <li>
            <p>
              <strong>Get a terminal with <Mono>nk</Mono>.</strong> The
              easiest is the <em>terminal to drive runs from</em> on the{" "}
              <Link to="/cpu">CPU page</Link>, where <Mono>nk</Mono> is
              preinstalled and already knows this console. On your own
              laptop, install it from the repository:
            </p>
            <CopyBlock
              value={'uv tool install "git+ssh://git@github.com/alexiswychovaniak/gpu-console.git#subdirectory=cli"'}
              label="Copy the nk install line"
              multiline
            />
          </li>
          <li>
            <p>
              <strong>Sign the terminal in:</strong>
            </p>
            <CopyBlock
              value={"nk login\nnk whoami"}
              label="Copy the login commands"
              multiline
            />
            <p>
              <Mono>nk login</Mono> asks for the key (and, on a laptop, for
              the console's address). <Mono>nk whoami</Mono> confirms who
              you are and which console answered.
            </p>
          </li>
          <li>
            <p>
              <strong>Smoke-test with two tiny tasks:</strong>
            </p>
            <CopyBlock
              value={'nk run --cores 1 --tasks 2 -- python3 -c "import os; print(\'I am task\', os.environ[\'TASK_ID\'])"'}
              label="Copy the smoke-test run"
              multiline
            />
            <p>
              Before submitting, <Mono>nk</Mono> shows what you are about
              to start and asks you to retype the number of tasks — a
              mistyped <Mono>--tasks</Mono> would be a thousand executions
              of something with side effects, so it asks once.
            </p>
          </li>
          <li>
            <p>
              <strong>Watch it, then read a task:</strong>
            </p>
            <CopyBlock
              value={"nk show <id> --follow\nnk logs <id> --task 0"}
              label="Copy the watch commands"
              multiline
            />
            <Output>{"I am task 0"}</Output>
            <p>
              <Mono>Ctrl-C</Mono> leaves <Mono>--follow</Mono> without
              touching the run.
            </p>
          </li>
        </ol>

        <h2>Splitting the work between tasks</h2>
        <p>
          Every task runs the <em>same command</em>; the environment tells
          it which share is its. The pattern that covers almost everything:
        </p>
        <CopyBlock
          value={
            "import os\n" +
            'task = int(os.environ["TASK_ID"])        # 0 … TASK_COUNT-1\n' +
            'count = int(os.environ["TASK_COUNT"])\n' +
            "mine = INPUTS[task::count]               # this task's share, no overlap"
          }
          label="Copy the sharding pattern"
          multiline
        />
        <p>
          Slurm-compatible means the variables, not the commands:{" "}
          <Mono>$SLURM_ARRAY_TASK_ID</Mono> and its array siblings are set
          to the same values, so a script that reads them to pick its shard
          runs unchanged — but there is no <Mono>srun</Mono>,{" "}
          <Mono>sbatch</Mono> or <Mono>squeue</Mono>; your command is the
          thing that runs. The group-of-processes family (
          <Mono>SLURM_NTASKS</Mono>, <Mono>MASTER_ADDR</Mono>,{" "}
          <Mono>RANK</Mono>, <Mono>WORLD_SIZE</Mono>) is deliberately
          absent: tasks never find each other, and setting those would
          switch some libraries into a distributed mode with no peers to
          reach.
        </p>

        <h2>Sizing a real campaign</h2>
        <ul>
          <li>
            <strong>Fewer, fatter tasks.</strong> Every task pays for its
            own start-up — a container, your imports, a model to load — so
            a hundred tasks of ten minutes beat a thousand tasks of one
            minute for the same work
            {cpu ? (
              <> (a run carries at most {cpu.max_tasks_per_run} tasks)</>
            ) : null}
            . Loop over shards inside the task instead of multiplying
            tasks.
          </li>
          <li>
            <strong>One task, one machine.</strong> No task can get more
            cores than the biggest machine offers — the refusal names the
            number when you ask past it — and a run coupled across machines
            (MPI, distributed training) is not what runs are for.
          </li>
          <li>
            <strong>
              Give the run the hours the whole campaign needs.
            </strong>{" "}
            {cpu ? (
              <>
                The deadline defaults to {cpu.run_hours} hours and can
                reach {cpu.max_run_hours}, extensions included.
              </>
            ) : (
              <>The default deadline is sized for a short run, not a campaign.</>
            )}{" "}
            A run bigger than the fleet runs <em>in waves, inside its
            deadline</em>: only so many tasks run at once, and the rest
            start as cores free up, in the same run. When the deadline
            lands, tasks still running are cut and tasks that never got a
            turn close as <em>not started</em> — never as failed, which is
            kept for your code failing — and the closing sentence gives
            all three counts. That is why the hours must cover the whole
            campaign, not the first wave; if some never started, read
            their indices from <Mono>nk tasks</Mono> and resubmit a run
            whose command maps <Mono>$TASK_ID</Mono> onto them. A run
            whose work finishes early ends early and gives the machines
            back; nothing stops a run for being idle, so an idle run also
            costs until its deadline.
          </li>
        </ul>

        <h2>Where the code comes from — and where results go</h2>
        <p>
          A run of more than one task starts on machines that have never
          seen your <Mono>/home</Mono>: its shared folder starts empty,
          and there is no upload step. Your code travels in the
          environment — bake it into{" "}
          <Link to="/docs/environments">an image of your own</Link> and
          pick it with <Mono>--image</Mono> — or the command fetches it
          itself (a <Mono>git clone</Mono> or a{" "}
          <Mono>pip install</Mono> from a repository, at the top of the
          command). A run of exactly one task is the exception: it runs in
          your <Mono>/home</Mono>, with everything already there.
        </p>
        <p className="docs-callout">
          A run of more than one task does <strong>not</strong> see your{" "}
          <Mono>/home</Mono>. Its machines share one folder,{" "}
          <Mono>/work</Mono>, identical on all of them — it starts empty
          and is <strong>deleted when the run ends</strong>, whether it
          succeeded, failed or was stopped. So push results out{" "}
          <em>before each task exits</em> — to MLflow, object storage, a
          repository — and remember that every one of those needs a
          credential the task must already carry:{" "}
          <Link to="/docs/mlflow">MLflow, end to end</Link> says how a
          fanned task does that, and the trap to avoid. Whatever is only
          in <Mono>/work</Mono> or <Mono>/scratch</Mono> at the end is
          gone.
        </p>
        <p>
          <Mono>/scratch</Mono> is private to each task — even two tasks of
          the same run on one machine do not share it — and goes when that
          task ends.
        </p>

        <h2>While it runs, and after</h2>
        <CopyBlock
          value={
            "nk runs                        # everything you have, newest first\n" +
            "nk show <id>                   # one run: its numbers and its story\n" +
            "nk tasks <id> --state failed   # which tasks failed\n" +
            "nk logs <id> --task 37         # one task's output\n" +
            "nk extend <id> --hours 2       # push the deadline back\n" +
            "nk stop <id>                   # stop every task of a run"
          }
          label="Copy the everyday nk commands"
          multiline
        />
        <p>
          If a task fails, its last log lines are kept so{" "}
          <Mono>nk logs</Mono> still answers after its machine is gone —
          for the first couple of hundred failures of one run, which is as
          many as the console stores; past that, the run says so. A task
          that succeeded keeps nothing. Retries exist but are opt-in (
          <Mono>--retries</Mono>), for tasks that are safe to re-run. One
          caveat on <Mono>nk stop</Mono>: read the failed logs first —
          stopping cuts every container at once, and a tail that had not
          been read yet goes with its machine.
        </p>
        <p>
          One courtesy: while a fleet-sized run holds every machine, other
          CPU submissions are refused until cores free up. Before launching
          a many-hour one, a word in the team channel spares a colleague a
          confusing refusal.
        </p>
      </>
    ),
  },
  // -------------------------------------------------------------- reference
  {
    id: "elsewhere",
    title: "Training elsewhere",
    lead: "Our MLflow from any cluster, and the door to Jean Zay.",
    group: "Reference",
    body: () => (
      <>
        <p>
          Training on somebody else's cluster goes through one machine of
          ours that sits on both sides of the private network. Everything
          you need is on the{" "}
          <Link to="/relay">Training elsewhere</Link> page, which prints
          the exact lines for <em>your</em> account — they are not repeated
          here, because that page shows what the server holds and never a
          guess.
        </p>
        <ol className="docs-steps">
          <li>
            <p>
              <strong>Mark a key for it.</strong> On the{" "}
              <Link to="/account">Account page</Link>, save a key with{" "}
              <em>training elsewhere</em> ticked — or widen one you already
              have. It lands on that machine about a minute later; nothing
              separate to request, being approved is all it takes.
            </p>
          </li>
          <li>
            <p>
              <strong>For MLflow from another cluster:</strong> copy the
              tunnel line from the{" "}
              <Link to="/relay">Training elsewhere</Link> page, run it on
              the machine your training is on, and export the tracking
              address it shows. This console's own machines — workspaces,
              jobs, run tasks — need no tunnel at all; they reach MLflow
              directly. The step-by-step version of both cases — with the
              credentials, the job-script pattern and the VPN for your
              laptop — is <Link to="/docs/mlflow">MLflow, end to end</Link>.
            </p>
          </li>
          <li>
            <p>
              <strong>If a tunnel misbehaves</strong>, try the page's plain{" "}
              <Mono>ssh</Mono> line first: if the shell works and the
              tunnel does not, the problem is the forward, not your
              access.
            </p>
          </li>
        </ol>
        <p>
          For the accounts the team has declared to IDRIS, the same machine
          is also the route to Jean Zay — with your <em>own</em> IDRIS
          account, which the console does not create and cannot reset. The
          page says whether that applies to you.
        </p>
      </>
    ),
  },
  {
    id: "mlflow",
    title: "MLflow, end to end",
    lead: "Log where the team reads — from a run, another cluster, or your laptop.",
    group: "Reference",
    body: ({ relay }) => {
      const mlflow = relay?.mlflow ?? null;
      return (
        <>
          <p>
            The team's MLflow is deliberately not on the internet: it lives
            on a private network, and being unreachable is what keeps it
            safe. There are two roads in, and neither needs administrator
            rights or anything installed as root. From a machine{" "}
            <em>outside</em> our network — another cluster's compute node,
            a login node — an SSH tunnel through our relay puts MLflow on
            that machine's own <Mono>localhost</Mono>. From your{" "}
            <em>laptop</em>, the VPN opens the browser view. Five minutes
            each, once.
          </p>
          <p>
            One case needs neither road: machines of <em>this</em> console
            — workspaces, jobs and run tasks — reach MLflow directly over
            the private route. Point <Mono>MLFLOW_TRACKING_URI</Mono> at
            the direct address from your access file, <em>with the same
            three account exports as step 3 below</em> — MLflow asks who
            you are on the private route too, and answers{" "}
            <Mono>401</Mono> to silence.
          </p>
          <p className="docs-callout">
            <strong>A run of more than one task and secrets.</strong> A
            fanned task starts with no home, no way to be handed a file,
            and no environment parameter — the console cannot give it your
            MLflow password quietly. Two honest options: bake the account
            into an environment image that only you use, or carry it in
            the command itself — and if a secret rides in the command,{" "}
            <strong>always pass <Mono>--name</Mono></strong>: the command
            is shown only to you, but a run's <em>default name is the
            command</em>, and names are visible to the whole team. If
            neither fits, the honest answer is that a fanned run logs to
            MLflow not at all — a run of one task keeps your{" "}
            <Mono>/home</Mono>, and everything in it.
          </p>

          <h2>The tunnel, in four steps</h2>
          <ol className="docs-steps">
            <li>
              <p>
                <strong>Check the relay recognises you.</strong> Run the
                plain <Mono>ssh</Mono> line from the{" "}
                <Link to="/relay">Training elsewhere</Link> page
                {relay?.key_on_file ? <> — for you it is:</> : <>.</>}
              </p>
              {relay?.key_on_file ? (
                <CopyBlock
                  value={relay.ssh_command}
                  label="Copy the relay ssh line"
                  multiline
                />
              ) : null}
              <p>
                A shell means you are in — go to step 2. A{" "}
                <Mono>Permission denied (publickey)</Mono> means no key of
                yours is on that machine yet: on the{" "}
                <Link to="/account">Account page</Link>, save a key with{" "}
                <em>training elsewhere</em> ticked — or widen one you have
                — and try again a minute later; the machine picks up the
                list about once a minute. A{" "}
                <Mono>Connection timed out</Mono> is <em>not</em> about
                your key: either that cluster's outbound address has not
                been allowed yet, or the relay itself is down. Report it in
                the team channel with the output of{" "}
                <Mono>curl -s https://api.ipify.org</Mono> — allowing a new
                address is a deployment on our side, not a one-click fix,
                so there is nothing to troubleshoot on yours.
              </p>
            </li>
            <li>
              <p>
                <strong>Open the tunnel</strong>, on the machine your code
                runs on
                {mlflow ? <> — the line the console builds for you:</> : (
                  <>
                    {" "}
                    — copy the tunnel line from the{" "}
                    <Link to="/relay">Training elsewhere</Link> page once
                    MLflow is configured there.
                  </>
                )}
              </p>
              {mlflow ? (
                <CopyBlock
                  value={mlflow.tunnel_command}
                  label="Copy the tunnel command"
                  multiline
                />
              ) : null}
              <p>
                The line carries <Mono>-L</Mono> (forwards the local port
                to MLflow through the relay), <Mono>-f -N</Mono> (stays in
                the background, opens no shell) and{" "}
                <Mono>ExitOnForwardFailure=yes</Mono>, which makes the
                command fail <em>now</em> if the forward cannot be opened —
                without it, <Mono>ssh</Mono> reports success anyway and the
                training finishes with the experiment logged into nothing.
              </p>
              {mlflow ? (
                <>
                  <p>Then prove the tunnel is really up:</p>
                  <CopyBlock
                    value={`curl -s -o /dev/null -w '%{http_code}\\n' http://localhost:${mlflow.local_port}/health`}
                    label="Copy the tunnel health check"
                    multiline
                  />
                  <Output>{"200"}</Output>
                </>
              ) : null}
            </li>
            <li>
              <p>
                <strong>Point your code at it.</strong>{" "}
                {mlflow ? (
                  <>The tracking address is the tunnel:</>
                ) : (
                  <>
                    The tracking address is the tunnel —{" "}
                    <Mono>http://localhost:&lt;the first number in your
                    -L flag&gt;</Mono>.
                  </>
                )}
              </p>
              {mlflow ? (
                <CopyBlock
                  value={mlflow.export_command}
                  label="Copy the tracking URI export"
                  multiline
                />
              ) : null}
              <p>
                MLflow also wants to know who you are — the account from
                your access file — and long uploads need a longer patience
                than the client's default two minutes, or a multi-gigabyte
                checkpoint dies mid-flight:
              </p>
              <CopyBlock
                value={
                  "export MLFLOW_TRACKING_USERNAME=<your_email@neuralk-ai.com>\n" +
                  "export MLFLOW_TRACKING_PASSWORD=<your_password>\n" +
                  "export MLFLOW_HTTP_REQUEST_TIMEOUT=1800"
                }
                label="Copy the MLflow account exports"
                multiline
              />
              <p>
                Put these in the file your shell actually reads on that
                machine — <Mono>~/.bashrc</Mono> there,{" "}
                <Mono>~/.zshrc.local</Mono> on a console workspace — or in
                the job script itself, and <Mono>chmod 600</Mono> whatever
                file holds the password. Never hard-code them in source,
                never commit them to a repository.
              </p>
            </li>
            <li>
              <p>
                <strong>Prove it end to end:</strong>
              </p>
              <CopyBlock
                value={
                  "import mlflow\n" +
                  'mlflow.set_experiment("tunnel-smoke-test")\n' +
                  "with mlflow.start_run():\n" +
                  '    mlflow.log_metric("hello", 1.0)'
                }
                label="Copy the MLflow smoke test"
                multiline
              />
              <p>
                The run appears in the team's MLflow. From here, nothing
                else in your code changes.
              </p>
            </li>
          </ol>

          <h2>In a job that lands on another node</h2>
          <p>
            The tunnel must live <strong>on the machine where the code
            runs</strong>. If your jobs are dispatched to other nodes, open
            it at the top of the job script — <Mono>ExitOnForwardFailure</Mono>{" "}
            is what turns "silently logging nowhere" into "fails before it
            starts":
          </p>
          {mlflow ? (
            <CopyBlock
              value={
                "#!/bin/bash\n" +
                "set -euo pipefail\n\n" +
                "# Reuse a live tunnel, open one only if needed — safe when jobs share a node.\n" +
                `curl -sf -o /dev/null http://localhost:${mlflow.local_port}/health || \\\n  ${mlflow.tunnel_command}\n\n` +
                `${mlflow.export_command}\n` +
                "export MLFLOW_TRACKING_USERNAME=<your_email@neuralk-ai.com>  # from your access file\n" +
                "export MLFLOW_TRACKING_PASSWORD=<your_password>              # never committed\n" +
                "export MLFLOW_HTTP_REQUEST_TIMEOUT=1800\n\n" +
                "python train.py"
              }
              label="Copy the job-script pattern"
              multiline
            />
          ) : (
            <p>
              The exact lines to paste are on the{" "}
              <Link to="/relay">Training elsewhere</Link> page once MLflow
              is configured there.
            </p>
          )}
          <p>
            For very long runs on a shaky network, <Mono>autossh</Mono>{" "}
            (where installed) reopens a dropped tunnel by itself — the same
            line, every flag kept, with <Mono>ssh</Mono> swapped out:
          </p>
          {mlflow ? (
            <CopyBlock
              value={mlflow.tunnel_command.replace(
                /^ssh /,
                "autossh -M 0 -o ServerAliveInterval=30 -o ServerAliveCountMax=3 ",
              )}
              label="Copy the autossh variant"
              multiline
            />
          ) : null}

          <h2>From your laptop: the VPN</h2>
          <p>
            The browser view — and code running on your laptop — go through
            the VPN rather than the tunnel. You will have received a
            personal encrypted file named <Mono>&lt;name&gt;.txt.age</Mono>{" "}
            holding your VPN configuration and your MLflow account.
          </p>
          <ol className="docs-steps">
            <li>
              <p>
                <strong>Open your access file.</strong> It is encrypted to
                your own SSH key, so nobody else can read it even if it
                leaks:
              </p>
              <CopyBlock
                value={
                  "brew install age\n" +
                  "age -d -i ~/.ssh/id_ed25519 <name>.txt.age > access.txt"
                }
                label="Copy the decryption commands"
                multiline
              />
              <p>
                If it answers{" "}
                <Mono>no identity matched any of the recipients</Mono>, the
                file was encrypted to the wrong key — say so instead of
                debugging it; re-issuing one takes two minutes.
              </p>
            </li>
            <li>
              <p>
                <strong>Connect the VPN.</strong> Install WireGuard (on
                macOS, from the App Store), import the configuration from
                your access file, click <em>Activate</em>. It is a split
                tunnel: your normal internet traffic and speed are
                untouched.
              </p>
            </li>
            <li>
              <p>
                <strong>Open MLflow</strong> at the address your access
                file names, and sign in with the credentials beside it.
                Code on the laptop uses the same address in{" "}
                <Mono>MLFLOW_TRACKING_URI</Mono>, with the same three
                exports as step 3 above.
              </p>
            </li>
          </ol>

          <h2>If something misbehaves</h2>
          <ul className="docs-fixes">
            <li>
              <p>
                <strong>
                  <Mono>curl localhost/health</Mono> answers{" "}
                  <Mono>000</Mono>.
                </strong>{" "}
                The tunnel is not open, or dropped. Run step 2 again;{" "}
                <Mono>ps aux | grep 'ssh -f -N'</Mono> shows whether one is
                alive.
              </p>
            </li>
            <li>
              <p>
                <strong><Mono>401 Unauthorized</Mono>.</strong> The account
                variables are not in <em>this</em> process. Check with{" "}
                <Mono>
                  python3 -c "import os;
                  print(os.environ.get('MLFLOW_TRACKING_USERNAME'))"
                </Mono>{" "}
                — exports live per shell, and a job script is not your
                login shell.
              </p>
            </li>
            <li>
              <p>
                <strong>The job seems to log nowhere.</strong> The tunnel
                is missing on the node the job actually ran on. Open it in
                the job script itself, with{" "}
                <Mono>ExitOnForwardFailure=yes</Mono>, as above.
              </p>
            </li>
            <li>
              <p>
                <strong>An upload stops after about two minutes.</strong>{" "}
                The client's default timeout. Export{" "}
                <Mono>MLFLOW_HTTP_REQUEST_TIMEOUT=1800</Mono> where the
                code runs.
              </p>
            </li>
            <li>
              <p>
                <strong><Mono>channel setup failed</Mono>.</strong> The
                local port is already taken on that machine. Pick another
                in the <Mono>-L</Mono> flag — the first number is yours to
                choose — and export the matching tracking address.
              </p>
            </li>
          </ul>

          <p className="docs-callout">
            <strong>Two rules.</strong> One MLflow account is one person —
            do not lend credentials to help a colleague debug; a new
            account takes two minutes. And never widen the tunnel to other
            machines (no <Mono>-L 0.0.0.0:…</Mono>): it is meant for your
            own session, and exposing it hands the whole cluster a door
            that is not theirs.
          </p>
        </>
      );
    },
  },
  {
    id: "limits",
    title: "What can refuse you — and what never will",
    lead: "A few mechanical guards, each of which names itself. No rationing.",
    group: "Reference",
    body: ({ cpu }) => (
      <>
        <p>
          There is no per-person share of GPUs, no weekly figure to save
          up, and no counter of hours anybody spends. Machines are elastic:
          ask for what the work needs, give it back when you are done. What
          bounds the bill is machinery, not arithmetic about you — fleet
          ceilings counted in machines, deadlines, idle reclaim, and an
          emergency stop.
        </p>
        <p>The guards you can actually meet, and what each one is:</p>
        <ul>
          <li>
            <strong>A handful of workspaces and jobs open at once</strong>{" "}
            per person — not about GPUs (any one of them can hold a whole
            machine); it keeps the finite SSH doors free, the refusal
            names the exact number, and stopping something you are done
            with makes room instantly.
          </li>
          <li>
            <strong>A small number of runs live or waiting</strong> per
            person — it counts runs, never cores: one run may still hold
            the whole fleet.
          </li>
          <li>
            <strong>
              {cpu ? `${cpu.max_tasks_per_run} tasks` : "A ceiling on tasks"}{" "}
              per run
            </strong>{" "}
            — a guard against a mistyped number, not against ambition;
            split a bigger campaign into several runs, or give each task
            more work.
          </li>
          <li>
            <strong>One task, one machine</strong> — no task can get more
            cores than the biggest machine offers, and a run coupled
            across machines (MPI, distributed training) is not what runs
            are for: a run is many independent copies of one command.
          </li>
          <li>
            <strong>One home, one machine at a time</strong> — a GPU
            workspace and a CPU workspace cannot be open at once; the
            refusal names what holds your files. Fanned runs are exempt.
          </li>
          <li>
            <strong>A ceiling on a CPU workspace's total time</strong>,
            extensions included — nothing can measure idleness on cores,
            so there the deadline is the only end, and it has to end
            somewhere. A GPU workspace has no such total: idle GPUs go
            back by themselves.
          </li>
        </ul>
        <p>
          Every refusal the console writes names the rule it comes from. A
          refusal that surprises you is worth reporting — it means either
          the rule or the sentence needs work.
        </p>
      </>
    ),
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    lead: "Symptom, cause, fix — and the screen that explains itself first.",
    group: "Reference",
    body: ({ contact, slackNotifications }) => (
      <>
        <p>
          Start with the <strong>What happened</strong> trace on the
          workspace or run screen — it is the console explaining itself in
          plain English, and if something stopped while you were away, the
          last line says why.
        </p>
        <ul className="docs-fixes">
          <li>
            <p>
              <strong>
                <Mono>Connection refused</Mono> on a just-opened workspace.
              </strong>{" "}
              Normal for up to a minute: the door opens shortly after the
              workspace itself. Wait a minute, try again. On a workspace
              that has been up a while, it is <em>not</em> normal — say so.
            </p>
          </li>
          <li>
            <p>
              <strong>My shell died out of nowhere.</strong> Almost always
              the deadline, or idle reclaim after a long stretch without
              GPU activity — both warn on the workspace page{" "}
              {slackNotifications
                ? "and on Slack, never in the terminal"
                : "and only there, never in the terminal"}{" "}
              — or the machine stopping
              everything at once because a program went past the memory the
              workspace holds, in which case the workspace started again by
              itself and you reconnect the same way (
              <Link to="/docs/memory">Memory</Link>). Read the What has
              happened trace; your <Mono>/home</Mono> is intact in every
              case.
            </p>
          </li>
          <li>
            <p>
              <strong>
                <Mono>pip install</Mono> refuses:{" "}
                <Mono>externally-managed-environment</Mono>.
              </strong>{" "}
              You are talking to the machine's own Python, which protects
              itself — and which was never the one to work in. Use{" "}
              <Mono>uv venv</Mono> + <Mono>uv pip install</Mono> in your
              project instead: everything lands in <Mono>/home</Mono> and
              survives.{" "}
              <Link to="/docs/python-envs">Python, with uv</Link> is the
              two-minute setup.
            </p>
          </li>
          <li>
            <p>
              <strong>
                I installed a newer tool and the old version keeps
                answering.
              </strong>{" "}
              Your install worked, and <Mono>~/.local/bin</Mono> already
              comes first on PATH — but the shell remembers where it{" "}
              <em>first</em> found a command. Run <Mono>rehash</Mono> (
              <Mono>hash -r</Mono> in bash) or open a new terminal, and
              the version you installed wins. This is the whole mystery
              behind "<Mono>uv --version</Mono> did not change".
            </p>
          </li>
          <li>
            <p>
              <strong>
                "The environment's image could not be downloaded."
              </strong>{" "}
              The reference is wrong, the tag was never pushed, or the
              machines may not pull from that registry. Check the
              reference first — this one is yours to fix.
            </p>
          </li>
          <li>
            <p>
              <strong>My run's files are gone.</strong> A run of more than
              one task works in <Mono>/work</Mono>, which is deleted when
              the run ends — that is by design, and the reason{" "}
              <Link to="/docs/runs">the runs page</Link> says to push
              results out before each task exits. What was pushed to
              MLflow, object storage or a repository is safe.
            </p>
          </li>
          <li>
            <p>
              <strong><Mono>sudo</Mono> asks for a password.</strong> There
              is none — that is a bug on our side, not yours. Say so, with
              the workspace link.
            </p>
          </li>
          <li>
            <p>
              <strong>Something asks me for a cloud credential.</strong>{" "}
              Then it is not this tool — you never need one here. Console
              restarts and updates never touch your workspace either:
              your run keeps running, SSH stays up.
            </p>
          </li>
        </ul>
        <p>
          Still stuck: tell{" "}
          {contact ? <Mono>{contact}</Mono> : "the team channel"}, with the
          workspace or run link and the last line of its What happened
          trace — that is enough to look it up.
        </p>
      </>
    ),
  },
];

/** The section after this one in reading order, or `null` at the end. */
export function nextSection(id: string): GuideSection | null {
  const index = GUIDE.findIndex((section) => section.id === id);
  return index >= 0 && index < GUIDE.length - 1 ? GUIDE[index + 1] : null;
}

/** The section before this one in reading order, or `null` at the start. */
export function previousSection(id: string): GuideSection | null {
  const index = GUIDE.findIndex((section) => section.id === id);
  return index > 0 ? GUIDE[index - 1] : null;
}
